// sim/genePowerBudget.ts — RELATIVE gene power-progression budget.
//
// THE PROBLEM IT SOLVES. The genome turns a gene into an ABSOLUTE channel vector
// (geneChannels) whose magnitude scales ~quadratically with rarity (more traits AND a
// bigger per-trait bonus at higher rarity — see geneData RARITY_TRAIT_ROLLS ×
// RARITY_MAG_BONUS). Against a small early-game caste profile that absolute injection is
// a 300–500% power spike; against a large late-game profile the SAME gene is a rounding
// error. Result: progression spikes, obsolete inventories, poor loot pacing, unstable
// scaling.
//
// THE MODEL. A newly applied gene should lift a caste's power by a RARITY-BANDED PERCENT
// of its CURRENT power — bounded, so it can never spike, and relative, so it stays
// meaningful at every stage of the game. The gene's raw roll only POSITIONS it within
// its band (a strong roll lands near the top, a weak one near the floor); it can never
// breach the ceiling. Rarity therefore governs the band (relative gain) and — via the
// gene's surviving traits and its ascension/fusion ceiling — its effects, synergy, and
// future scaling, NOT a runaway raw number.
//
// DIMINISHING RETURNS (anti-exponential). A pure relative-% band still COMPOUNDS: each
// apply multiplies power by (1 + gainPct/100), so over a long session power grows
// geometrically (exponentially). `diminishFactor` bends the gain by KNEE/(KNEE+power) so
// the gain stays relative early (power still doubles fast — a major early step) and tapers
// to a roughly FIXED ABSOLUTE amount late (a small relative crawl). Result: infinite
// progression at a BOUNDED, ~linear pace — 100→200 feels major, 5000→5100 feels minor —
// without ever capping. The ceiling is unaffected (diminishing only reduces the gain).
//
// PURE + DETERMINISTIC. Integer-friendly arithmetic, no RNG, no DOM, no sim mutation. The
// caller injects an `evalDelta(channels)` that reports the realized caste-power delta a
// candidate channel vector would add (game.ts wires it to genome.previewProfile +
// casteProfilePower); this module never imports the genome or the power read-model, so it
// stays a self-contained, headlessly-testable formula (src/sim/genePowerBudget.test.mjs).

import { RARITY } from '../types.ts';

// ---------------------------------------------------------------------------
// Bands: [lo, hi] PERCENT of current caste power a freshly applied gene adds,
// indexed by effective tier. Tiers 0..4 are the gene rarities (Common…Legendary);
// tier 5 ("Apex") is reserved for ascended / fused / miracle-tier genes that have
// climbed past Legendary (see effectiveTier). The ceilings are HARD — no roll, no
// matter how lucky, may exceed its band's hi (this is the anti-spike guarantee).
// ---------------------------------------------------------------------------

// RARITY REFACTOR: the bands are deliberately COMPRESSED. Rarity used to be a ~5× power
// multiplier here (Common 0–3% vs Legendary 12–20%), which made a rare drop "merely
// stronger". Rarity now buys only a MODEST power edge (~2× across the whole range); its
// real value lives elsewhere — more trait rolls (geneData RARITY_TRAIT_ROLLS) → more
// synergy, and the qualitative effect ladder (src/sim/rarityPerks.ts). Tunable round
// numbers; keep the ceilings strictly rising (the anti-spike contract the test pins).
export const RARITY_GAIN_BAND: readonly (readonly [number, number])[] = [
  [2, 5],    // Common
  [3, 6],    // Uncommon
  [4, 8],    // Rare
  [5, 9],    // Epic
  [6, 11],   // Legendary
  [8, 13],   // Apex (ascended / fused / miracle-tier)
];

/** Effective-tier index of the top "Apex" band. */
export const APEX_TIER = RARITY_GAIN_BAND.length - 1;

/**
 * Raw %-jump that maps a roll to the MIDPOINT of its band. The band position saturates
 * as `rawPct / (rawPct + REF)`, so a modest roll sits mid-band and an absurd 300–500%
 * raw jump is compressed asymptotically toward (never past) the band's hi.
 */
export const POSITION_REF_PCT = 25;

/**
 * Ceiling on how far a weak roll's channels may be amplified to reach its band FLOOR.
 * Compression (killing spikes) is unbounded; amplification is capped so lifting a feeble
 * gene toward its floor can't distort its in-sim channel magnitudes (and behaviour) wildly.
 * Consequence: the band CEILING is a hard guarantee; the band FLOOR is a soft target a
 * pathologically weak roll may land just under.
 */
export const MAX_AMPLIFY = 4;

/** Fixed bisection steps for the channel scaler — deterministic, allocation-light. */
const BISECT_STEPS = 26;

// ---------------------------------------------------------------------------
// Pure band math (no channel/profile dependency — directly unit-testable).
// ---------------------------------------------------------------------------

/**
 * Effective tier for a gene. Rarities map 1:1 to tiers 0..4; a Legendary that has been
 * ASCENDED or FUSED (ascension ≥ 1) graduates into the top "Apex" band, so long-term
 * progression has somewhere to climb without breaking the per-apply ceiling.
 */
export function effectiveTier(rarity: number, ascension = 0): number {
  if (ascension > 0 && rarity >= RARITY.Legendary) return APEX_TIER;
  return rarity < 0 ? 0 : rarity > APEX_TIER ? APEX_TIER : rarity | 0;
}

/** The [lo, hi] percent band for an effective tier (clamped to the table). */
export function bandFor(tier: number): readonly [number, number] {
  const t = tier < 0 ? 0 : tier >= RARITY_GAIN_BAND.length ? RARITY_GAIN_BAND.length - 1 : tier | 0;
  return RARITY_GAIN_BAND[t] as readonly [number, number];
}

/**
 * Position a roll within its band from its raw %-jump and return the target gain PERCENT.
 * `rawPct` ≤ 0 → the band floor; larger raw jumps saturate toward (but never reach) the
 * band's hi. The return is always within [lo, hi) — the heart of the anti-spike guarantee.
 */
export function gainPercent(tier: number, rawPct: number): number {
  const [lo, hi] = bandFor(tier);
  if (rawPct <= 0) return lo;
  const pos = rawPct / (rawPct + POSITION_REF_PCT); // 0..1, saturating
  return lo + (hi - lo) * pos;
}

/**
 * Soft knee (in CASTE-power units) where per-apply gains start diminishing. At
 * `currentPower === POWER_DIMINISH_KNEE` a gain is halved; far below it the gain stays
 * fully relative, far above it it shrinks ~1/power. Tunable balance knob.
 */
export const POWER_DIMINISH_KNEE = 1500;

/**
 * DIMINISHING-RETURNS factor on the per-apply gain, so infinite progression never
 * compounds into exponential runaway.
 *
 * Why it is needed: the band is a PERCENT of current power, so applying gene after gene
 * multiplies power by `(1 + gainPct/100)` each time — GEOMETRIC, i.e. exponential, growth
 * over a long session. Multiplying the gain by `KNEE / (KNEE + currentPower)` makes the
 * EFFECTIVE base saturate at the knee: below it the gain is ~fully relative (early power
 * still doubles fast — a big, exciting step), above it the gain tapers to a roughly FIXED
 * ABSOLUTE amount (`≈ KNEE · gainPct/100`), so late-game power climbs ~LINEARLY — a small
 * relative crawl (e.g. 5000 → ~5130 per Legendary) that never stops but never spikes.
 * Pure, deterministic, ∈ (0, 1]; 1 when there is no relative frame.
 */
export function diminishFactor(currentPower: number): number {
  if (currentPower <= 0) return 1;
  return POWER_DIMINISH_KNEE / (POWER_DIMINISH_KNEE + currentPower);
}

/**
 * Target power delta (absolute, integer) a freshly applied gene should add: a
 * rarity-banded PERCENT of `currentPower`, positioned by the gene's unscaled `rawDelta`,
 * then bent by the diminishing-returns factor so gains taper as a caste grows (the
 * anti-exponential contract). With no relative frame (currentPower ≤ 0) it falls back to
 * the raw delta. Diminishing only REDUCES the gain, so the band ceiling still holds.
 */
export function targetGain(currentPower: number, tier: number, rawDelta: number): number {
  if (currentPower <= 0) return Math.max(0, Math.round(rawDelta));
  const rawPct = rawDelta <= 0 ? 0 : (100 * rawDelta) / currentPower;
  const banded = (currentPower * gainPercent(tier, rawPct)) / 100;
  return Math.round(banded * diminishFactor(currentPower));
}

/**
 * The PROJECTED gain percent for a UI forecast ("≈ +6% Soldier power"): the same band
 * position AND diminishing factor the apply will use, so the preview and the result agree
 * (the forecast shrinks with power exactly as the real gain does). 0 with no relative frame.
 */
export function projectedGainPercent(currentPower: number, tier: number, rawDelta: number): number {
  if (currentPower <= 0) return 0;
  const rawPct = rawDelta <= 0 ? 0 : (100 * rawDelta) / currentPower;
  return gainPercent(tier, rawPct) * diminishFactor(currentPower);
}

// ---------------------------------------------------------------------------
// Channel scaler. Bisection on a single scale factor f, so the gene KEEPS ITS
// IDENTITY (which stats it boosts, and its trade-off penalties) while its overall
// magnitude is bent to the budget.
// ---------------------------------------------------------------------------

/** Scale an integer channel vector by f, rounding to ints (sign/identity preserved). */
function scaleInto(raw: readonly number[], f: number, out: number[]): number[] {
  for (let i = 0; i < raw.length; i++) out[i] = Math.round((raw[i] as number) * f);
  return out;
}

/**
 * Bend a gene's raw channel vector to the power budget. Returns the integer-scaled
 * channels whose realized power delta is as close to `target` as quantization allows
 * WITHOUT exceeding it — so the apply can never spike past its band. `evalDelta(channels)`
 * reports the caste-power delta a candidate vector would add (monotonic non-decreasing in
 * the scale factor for a net-positive gene, which is the bisection's contract).
 *
 * Deterministic: a fixed `BISECT_STEPS` search on f ∈ [0, MAX_AMPLIFY]. f < 1 COMPRESSES a
 * spiky roll down to its ceiling; f > 1 AMPLIFIES a weak roll up toward its floor (bounded
 * by MAX_AMPLIFY). A non-positive target (e.g. a Common against a tiny colony, or a gene
 * that is a net sidegrade/penalty) yields the zero vector — no forced change.
 */
export function scaleChannelsToBudget(
  raw: readonly number[],
  target: number,
  evalDelta: (ch: readonly number[]) => number,
): number[] {
  const out = new Array<number>(raw.length).fill(0);
  if (target <= 0) return out;
  // If even the max-amplified gene stays within budget, take it (a weak roll vs its band).
  if (evalDelta(scaleInto(raw, MAX_AMPLIFY, out)) <= target) return out.slice();
  // Otherwise bisect for the largest f whose realized delta does not exceed the target.
  let lo = 0;             // f=0 ⇒ delta 0 ≤ target (always true)
  let hi = MAX_AMPLIFY;   // f=MAX_AMPLIFY ⇒ delta > target (just established)
  for (let i = 0; i < BISECT_STEPS; i++) {
    const mid = (lo + hi) / 2;
    if (evalDelta(scaleInto(raw, mid, out)) <= target) lo = mid;
    else hi = mid;
  }
  return scaleInto(raw, lo, out).slice();
}
